import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate, useParams } from 'react-router-dom';
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import DomainAssetModal from '../components/assets/DomainAssetModal';
import ServerAssetModal from '../components/assets/ServerAssetModal';
import OtherAssetModal from '../components/assets/OtherAssetModal';
import TelegramAssetModal from '../components/assets/TelegramAssetModal';
import ViewDomainAssetModal from '../components/assets/ViewDomainAssetModal';
import ViewServerAssetModal from '../components/assets/ViewServerAssetModal';
import ViewOtherAssetModal from '../components/assets/ViewOtherAssetModal';
import ViewTelegramAssetModal from '../components/assets/ViewTelegramAssetModal';
import {
    API_BASE,
    authHeaders,
    formatSgDate,
    formatAssetStatus,
    boolTickChar,
    boolTickClass,
    formatDnsDisplay,
    formatDomainServerLinks,
    formatDomainRedirectTarget,
    formatTelegramLabel,
} from '../utils/assetHelpers';

const VALID_TABS = ['domains', 'servers', 'telegram', 'others'];

const emptyMeta = {
    statuses: [],
    domain: { dns: [], dnsAccounts: [], currencies: [] },
    server: {
        serverTypes: [],
        paymentTypes: [],
        paymentCards: [],
        currencies: [],
        subServerTypes: [],
        estMonthlyPaymentCurrencies: [],
    },
    other: { assetTypes: [], paymentTypes: [], projectCategories: [] },
    telegram: { types: [], statuses: [], communities: [], categories: [] },
    servers: [],
    domainAssets: [],
    telegramAssets: [],
};

const AssetPageContent = ({ activeTab }) => {
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState(emptyMeta);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [extraFilter, setExtraFilter] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [expandedServerRows, setExpandedServerRows] = useState(new Set());

    const apiSegment = activeTab;

    const fetchMeta = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/assets/meta`, { headers: authHeaders() });
            if (response.data.success) setMeta({ ...emptyMeta, ...response.data.data });
        } catch {
            setMeta(emptyMeta);
        }
    };

    const fetchItems = async (page = 1, search = '', status = '', extra = '') => {
        try {
            setLoading(true);
            let apiUrl = `${API_BASE}/api/assets/${apiSegment}?page=${page}&limit=20&sort=-createdAt`;
            if (search) apiUrl += `&search=${encodeURIComponent(search)}`;
            if (status) apiUrl += `&status=${encodeURIComponent(status)}`;
            if (activeTab === 'others' && extra) apiUrl += `&assetType=${encodeURIComponent(extra)}`;
            if (activeTab === 'servers' && extra) apiUrl += `&serverType=${encodeURIComponent(extra)}`;
            if (activeTab === 'domains' && extra) apiUrl += `&DNS=${encodeURIComponent(extra)}`;
            if (activeTab === 'telegram' && extra) apiUrl += `&type=${encodeURIComponent(extra)}`;

            const response = await axios.get(apiUrl, { headers: authHeaders() });
            if (response.data.success) {
                setItems(response.data.data);
                setTotalItems(response.data.pagination.total);
                setTotalPages(response.data.pagination.pages);
            }
        } catch (err) {
            setError(`Failed to fetch ${activeTab}: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMeta();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
        setSearchInput('');
        setSearchTerm('');
        setStatusFilter('');
        setExtraFilter('');
        setSelectedItem(null);
        setShowCreateModal(false);
        setShowEditModal(false);
        setShowViewModal(false);
        setShowDeleteModal(false);
        setError('');
    }, [activeTab]);

    useEffect(() => {
        fetchItems(currentPage, searchTerm, statusFilter, extraFilter);
    }, [activeTab, currentPage, searchTerm, statusFilter, extraFilter]);

    useEffect(() => {
        if (activeTab === 'servers' && items.length > 0) {
            setExpandedServerRows(new Set(items.map((item) => item._id)));
        }
    }, [activeTab, items]);

    const toggleServerRow = (serverId) => {
        setExpandedServerRows((prev) => {
            const next = new Set(prev);
            if (next.has(serverId)) next.delete(serverId);
            else next.add(serverId);
            return next;
        });
    };

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setStatusFilter('');
        setExtraFilter('');
        setCurrentPage(1);
    };

    const handleDelete = async () => {
        if (!selectedItem) return;
        try {
            setDeleteLoading(true);
            await axios.delete(`${API_BASE}/api/assets/${apiSegment}/${selectedItem._id}`, { headers: authHeaders() });
            setShowDeleteModal(false);
            setSelectedItem(null);
            fetchItems(currentPage, searchTerm, statusFilter, extraFilter);
            fetchMeta();
        } catch (err) {
            setError(`Failed to delete: ${err.response?.data?.error || err.message}`);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleSaved = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        setSelectedItem(null);
        fetchItems(currentPage, searchTerm, statusFilter, extraFilter);
        fetchMeta();
    };

    const getItemLabel = (item) => {
        if (activeTab === 'domains') return item.domainURL;
        if (activeTab === 'servers') return item.mainServerName;
        if (activeTab === 'telegram') return item.groupLink || formatTelegramLabel(item);
        return item.name || item.assetType;
    };

    const extraFilterOptions = () => {
        if (activeTab === 'domains') return meta.domain?.dns || [];
        if (activeTab === 'servers') return meta.server?.serverTypes || [];
        if (activeTab === 'telegram') return meta.telegram?.types || [];
        return meta.other?.assetTypes || [];
    };

    const extraFilterLabel = () => {
        if (activeTab === 'domains') return 'All DNS';
        if (activeTab === 'servers') return 'All Server Types';
        if (activeTab === 'telegram') return 'All Types';
        return 'All Asset Types';
    };

    const renderTableHead = () => {
        if (activeTab === 'domains') {
            return (
                <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domain URL</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Redirect To</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Server</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DNS</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">CF</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Auto Renew</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
            );
        }
        if (activeTab === 'servers') {
            return (
                <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8" />
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Server Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
            );
        }
        if (activeTab === 'telegram') {
            return (
                <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group Link</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Community</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Owner</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Private</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
            );
        }
        return (
            <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Owner</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
        );
    };

    const renderDomainRow = (item) => (
        <tr key={item._id} className="hover:bg-gray-50">
            <td className="px-4 py-3 text-sm text-blue-700 font-bold">{item.domainURL || '—'}</td>
            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatAssetStatus(item.status)}</td>
            <td className="px-4 py-3 text-sm text-gray-700">{formatDomainRedirectTarget(item)}</td>
            <td className="px-4 py-3 text-sm text-gray-700 whitespace-pre-line">{formatDomainServerLinks(item)}</td>
            <td className="px-4 py-3 text-sm text-gray-700">{formatDnsDisplay(item.DNS, item.DNSAccount)}</td>
            <td className="px-4 py-3 text-sm text-gray-700">{formatSgDate(item.expiryDate)}</td>
            <td className="px-4 py-3 text-sm text-center">
                <span className={boolTickClass(item.inCloudflare)}>{boolTickChar(item.inCloudflare)}</span>
            </td>
            <td className="px-4 py-3 text-sm text-center">
                <span className={boolTickClass(item.autoRenew)}>{boolTickChar(item.autoRenew)}</span>
            </td>
            {renderActions(item)}
        </tr>
    );

    const renderServerRows = (item) => {
        const subServers = item.subServerObject || [];
        const isExpanded = expandedServerRows.has(item._id);
        const hasSubServers = subServers.length > 0;

        return (
            <React.Fragment key={item._id}>
                <tr className="hover:bg-gray-50 bg-white">
                    <td className="px-4 py-3 text-sm">
                        {hasSubServers ? (
                            <button
                                type="button"
                                onClick={() => toggleServerRow(item._id)}
                                className="text-gray-500 hover:text-gray-800"
                                title={isExpanded ? 'Collapse sub servers' : 'Expand sub servers'}
                            >
                                {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                            </button>
                        ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-blue-700 font-bold">{item.mainServerName || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.serverType || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatAssetStatus(item.status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                        {item.needPayment && <span className="text-green-600 font-bold mr-1">✓</span>}
                        {(() => {
                            const payment = item.paymentObject || {};
                            const cardExpiry = [payment.cardExpiry?.month, payment.cardExpiry?.year].filter(Boolean).join('/');
                            const detail = [payment.paymentType, payment.paymentCard, cardExpiry].filter(Boolean).join(' - ');
                            return detail || (item.needPayment ? '' : '—');
                        })()}
                    </td>
                    {renderActions(item)}
                </tr>
                {isExpanded && hasSubServers && (
                    <tr>
                        <td colSpan={6} className="px-4 py-2">
                            <div className="overflow-x-auto bg-slate-50 rounded-lg border border-slate-200">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-slate-100">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sub Server</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Est. Amount</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Domains</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200">
                                        {subServers.map((sub, idx) => {
                                            const domainLines = (sub.domains || [])
                                                .map((d) => (typeof d === 'object' ? d.domainURL : d))
                                                .filter(Boolean);
                                            const estLabel = [sub.estMonthlyPaymentCurrency, sub.estMonthlyPaymentAmount]
                                                .filter((v) => v !== '' && v != null)
                                                .join(' ');
                                            return (
                                                <tr key={sub._id || idx}>
                                                    <td className="px-3 py-2 text-gray-800">{sub.name || '—'}</td>
                                                    <td className="px-3 py-2 text-gray-700">{sub.type || '—'}</td>
                                                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatAssetStatus(sub.status)}</td>
                                                    <td className="px-3 py-2 text-gray-700">{estLabel || '—'}</td>
                                                    <td className="px-3 py-2 text-gray-700 whitespace-pre-line">{domainLines.join('\n') || '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    const renderRow = (item) => {
        if (activeTab === 'domains') return renderDomainRow(item);
        if (activeTab === 'servers') return renderServerRows(item);
        if (activeTab === 'telegram') {
            return (
                <tr key={item._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-blue-700 font-bold">{item.groupLink || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.type || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{(item.community || []).join(', ') || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatAssetStatus(item.status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatTelegramLabel(item.owner)}</td>
                    <td className="px-4 py-3 text-sm text-center">
                        <span className={boolTickClass(item.isPrivate)}>{boolTickChar(item.isPrivate)}</span>
                    </td>
                    {renderActions(item)}
                </tr>
            );
        }
        return (
            <tr key={item._id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-blue-700 font-bold">{item.name || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{item.assetType || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{item.status || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{item.ownedBy || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{item.projectCategory || '-'}</td>
                {renderActions(item)}
            </tr>
        );
    };

    const renderActions = (item) => (
        <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
            <button onClick={() => { setSelectedItem(item); setShowViewModal(true); }} className="text-blue-600 hover:text-blue-800 mr-3" title="View"><FiEye /></button>
            <button onClick={() => { setSelectedItem(item); setShowEditModal(true); }} className="text-green-600 hover:text-green-800 mr-3" title="Edit"><FiEdit2 /></button>
            <button onClick={() => { setSelectedItem(item); setShowDeleteModal(true); }} className="text-red-600 hover:text-red-800" title="Delete"><FiTrash2 /></button>
        </td>
    );

    const colSpan = activeTab === 'domains' ? 9 : activeTab === 'servers' ? 6 : activeTab === 'telegram' ? 7 : 6;

    const pageTitle = {
        domains: 'Domain Assets',
        servers: 'Server Assets',
        telegram: 'Telegram Assets',
        others: 'Other Assets',
    }[activeTab] || 'Assets';

    const createLabel = {
        domains: 'Domain',
        servers: 'Server',
        telegram: 'Telegram Asset',
        others: 'Other Asset',
    }[activeTab] || 'Asset';

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">{pageTitle}</h1>
                        <p className="text-gray-600">Total: {totalItems} {activeTab}</p>
                    </div>
                    <button onClick={() => setShowCreateModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center self-start">
                        <FiPlus className="mr-2" />
                        Create {createLabel}
                    </button>
                </div>

                <div className="mb-6 flex flex-col lg:flex-row gap-2">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-blue-700 font-bold"
                            placeholder="Search..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <FiSearch className="h-5 w-5 text-gray-400" />
                        </div>
                    </div>
                    <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 rounded-md text-blue-700 font-bold min-w-[160px]">
                        <option value="">All Statuses</option>
                        {(meta.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={extraFilter} onChange={(e) => { setExtraFilter(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 rounded-md text-blue-700 font-bold min-w-[180px]">
                        <option value="">{extraFilterLabel()}</option>
                        {extraFilterOptions().map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    <button onClick={handleSearch} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"><FiSearch className="mr-2" />Search</button>
                    <button onClick={handleClear} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">Clear</button>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                {showCreateModal && activeTab === 'domains' && (
                    <DomainAssetModal mode="create" meta={meta} onSuccess={handleSaved} onCancel={() => setShowCreateModal(false)} />
                )}
                {showCreateModal && activeTab === 'servers' && (
                    <ServerAssetModal mode="create" meta={meta} onSuccess={handleSaved} onCancel={() => setShowCreateModal(false)} />
                )}
                {showCreateModal && activeTab === 'others' && (
                    <OtherAssetModal mode="create" meta={meta} onSuccess={handleSaved} onCancel={() => setShowCreateModal(false)} />
                )}
                {showCreateModal && activeTab === 'telegram' && (
                    <TelegramAssetModal mode="create" meta={meta} onSuccess={handleSaved} onCancel={() => setShowCreateModal(false)} />
                )}

                {showEditModal && selectedItem && activeTab === 'domains' && (
                    <DomainAssetModal mode="edit" asset={selectedItem} meta={meta} onSuccess={handleSaved} onCancel={() => { setShowEditModal(false); setSelectedItem(null); }} />
                )}
                {showEditModal && selectedItem && activeTab === 'servers' && (
                    <ServerAssetModal mode="edit" asset={selectedItem} meta={meta} onSuccess={handleSaved} onCancel={() => { setShowEditModal(false); setSelectedItem(null); }} />
                )}
                {showEditModal && selectedItem && activeTab === 'others' && (
                    <OtherAssetModal mode="edit" asset={selectedItem} meta={meta} onSuccess={handleSaved} onCancel={() => { setShowEditModal(false); setSelectedItem(null); }} />
                )}
                {showEditModal && selectedItem && activeTab === 'telegram' && (
                    <TelegramAssetModal mode="edit" asset={selectedItem} meta={meta} onSuccess={handleSaved} onCancel={() => { setShowEditModal(false); setSelectedItem(null); }} />
                )}

                {showViewModal && selectedItem && activeTab === 'domains' && (
                    <ViewDomainAssetModal asset={selectedItem} onClose={() => { setShowViewModal(false); setSelectedItem(null); }} onEdit={(item) => { setShowViewModal(false); setSelectedItem(item); setShowEditModal(true); }} />
                )}
                {showViewModal && selectedItem && activeTab === 'servers' && (
                    <ViewServerAssetModal asset={selectedItem} onClose={() => { setShowViewModal(false); setSelectedItem(null); }} onEdit={(item) => { setShowViewModal(false); setSelectedItem(item); setShowEditModal(true); }} />
                )}
                {showViewModal && selectedItem && activeTab === 'others' && (
                    <ViewOtherAssetModal asset={selectedItem} onClose={() => { setShowViewModal(false); setSelectedItem(null); }} onEdit={(item) => { setShowViewModal(false); setSelectedItem(item); setShowEditModal(true); }} />
                )}
                {showViewModal && selectedItem && activeTab === 'telegram' && (
                    <ViewTelegramAssetModal asset={selectedItem} onClose={() => { setShowViewModal(false); setSelectedItem(null); }} onEdit={(item) => { setShowViewModal(false); setSelectedItem(item); setShowEditModal(true); }} />
                )}

                {showDeleteModal && selectedItem && (
                    <DeleteConfirmationModal
                        title="Delete Asset"
                        message="Are you sure you want to delete this asset?"
                        itemName={getItemLabel(selectedItem) || selectedItem._id}
                        isLoading={deleteLoading}
                        onConfirm={handleDelete}
                        onCancel={() => { setShowDeleteModal(false); setSelectedItem(null); }}
                    />
                )}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">{renderTableHead()}</thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan={colSpan} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={colSpan} className="px-4 py-8 text-center text-gray-500">No records found.</td></tr>
                            ) : (
                                items.map(renderRow)
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="mt-6">
                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                    </div>
                )}
            </div>
        </Layout>
    );
};

const AssetPage = () => {
    const { tab } = useParams();
    if (!VALID_TABS.includes(tab)) {
        return <Navigate to="/assets/domains" replace />;
    }
    return <AssetPageContent activeTab={tab} />;
};

export default AssetPage;
